import React, { useMemo, useState, useRef, useEffect } from 'react';
import { CheckCircle2, Circle, ChevronRight, BookOpen, Target, MessageSquare, Zap, ChevronDown } from 'lucide-react';
import { useLocalizedNavigate } from '../../hooks/useLocalizedNavigate';
import { useTranslation } from 'react-i18next';

const INITIAL_VISIBLE = 3;

/* Animated container that transitions height 0 → measured height */
const ExpandSection = ({ expanded, children }) => {
  const ref = useRef(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (!ref.current) return;
    if (expanded) {
      // measure, then animate to full height
      setHeight(ref.current.scrollHeight);
    } else {
      // collapse: first snap to current pixel height so transition works
      setHeight(ref.current.scrollHeight);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setHeight(0));
      });
    }
  }, [expanded]);

  return (
    <div
      style={{
        height: expanded ? (height || 'auto') : height,
        overflow: 'hidden',
        transition: 'height 320ms cubic-bezier(0.4, 0, 0.2, 1)',
      }}
      onTransitionEnd={() => {
        if (expanded && ref.current) setHeight(ref.current.scrollHeight);
      }}
    >
      <div ref={ref}>{children}</div>
    </div>
  );
};

const RecommendedSessionBanner = ({ personalizedPlan, examData }) => {
  const navigate = useLocalizedNavigate();
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const planData = useMemo(() => {
    if (!personalizedPlan?.weeks?.length) return null;
    const currentWeekIndex = 0;
    const currentWeek = personalizedPlan.weeks[currentWeekIndex];
    if (!currentWeek) return null;
    const allTasks = currentWeek.tasks || [];
    return {
      currentWeekNumber: currentWeekIndex + 1,
      totalWeeks: personalizedPlan.totalWeeks,
      allTasks,
    };
  }, [personalizedPlan, examData]);

  if (!planData || planData.allTasks.length === 0) {
    return null;
  }

  const getTaskTypeLabel = (type) => {
    const labels = {
      vocabulary: t('practice.recommendedBanner.taskType.vocabulary'),
      grammar_lessons: t('practice.recommendedBanner.taskType.grammar'),
      grammar_exercises: t('practice.recommendedBanner.taskType.exercises'),
      redemittel: t('practice.recommendedBanner.taskType.redemittel'),
      exam_lessons: t('practice.recommendedBanner.taskType.lessons'),
      lessons: t('practice.recommendedBanner.taskType.lessons'),
      exam_simulation: t('practice.recommendedBanner.taskType.simulation'),
      lesen_practice: t('practice.recommendedBanner.taskType.lesen', { defaultValue: 'Reading Practice' }),
      horen_practice: t('practice.recommendedBanner.taskType.horen', { defaultValue: 'Listening Practice' }),
      schreiben_practice: t('practice.recommendedBanner.taskType.schreiben', { defaultValue: 'Writing Practice' }),
    };
    return labels[type] || type;
  };

  const getTaskIcon = (type) => {
    switch (type) {
      case 'vocabulary':
        return <BookOpen className="w-4 h-4 text-black/50" />;
      case 'grammar_lessons':
      case 'grammar_exercises':
        return <Target className="w-4 h-4 text-black/50" />;
      case 'redemittel':
        return <MessageSquare className="w-4 h-4 text-black/50" />;
      case 'lesen_practice':
        return <BookOpen className="w-4 h-4 text-black/50" />;
      case 'horen_practice':
      case 'schreiben_practice':
        return <Zap className="w-4 h-4 text-black/50" />;
      default:
        return <Zap className="w-4 h-4 text-black/50" />;
    }
  };

  const navigateToTask = (task) => {
    const pathMap = {
      vocabulary: '/practice/vocabulary',
      grammar_lessons: '/study/grammar',
      grammar_exercises: '/practice/grammar',
      redemittel: '/practice/redemittel',
      exam_lessons: '/lessons',
      lessons: '/lessons',
      exam_simulation: '/exam',
      lesen_practice: '/practice/reading',
      horen_practice: '/practice/listening',
      schreiben_practice: '/practice/writing',
    };
    navigate(pathMap[task.type] || '/practice');
  };

  const getCategoriesLine = (task) => {
    if (task.topics?.length) return task.topics.map((t) => t.name).join(', ');
    if (task.pillars?.length) return task.pillars.map((p) => p.name).join(', ');
    if (task.lessons?.length) return task.lessons.slice(0, 3).map((l) => l.title || l.lessonId).join(', ');
    if (task.type === 'redemittel' && (task.writing > 0 || task.speaking > 0)) {
      const parts = [];
      if (task.writing > 0) parts.push(`${task.writing} Writing`);
      if (task.speaking > 0) parts.push(`${task.speaking} Speaking`);
      return parts.join(' · ');
    }
    if (task.type === 'grammar_exercises' && task.exerciseCount) return `${task.exerciseCount} exercises`;
    if (task.description) {
      const maxLen = 50;
      const plain = task.description.replace(/\s+/g, ' ').trim();
      return plain.length <= maxLen ? plain : plain.slice(0, maxLen) + '…';
    }
    return getTaskTypeLabel(task.type);
  };

  const visibleTasks = planData.allTasks.slice(0, INITIAL_VISIBLE);
  const hiddenTasks = planData.allTasks.slice(INITIAL_VISIBLE);
  const hasMore = hiddenTasks.length > 0;

  const renderTaskRow = (task, index, isHidden = false) => {
    const categoriesLine = getCategoriesLine(task);
    return (
      <li
        key={task.id}
        style={
          isHidden && expanded
            ? {
                animation: `taskFadeSlideIn 280ms cubic-bezier(0.4, 0, 0.2, 1) ${index * 50}ms both`,
              }
            : undefined
        }
      >
        <button
          type="button"
          onClick={() => navigateToTask(task)}
          className="w-full flex items-center gap-3 px-4 py-3 sm:py-3.5 min-h-[48px] text-left hover:bg-black/[0.02] active:bg-black/[0.04] transition-colors touch-manipulation"
        >
          <span className="flex-shrink-0 mt-0.5">
            {task.completed ? (
              <CheckCircle2 className="w-5 h-5 text-black/40" />
            ) : (
              <Circle className="w-5 h-5 text-black/30" strokeWidth={2} />
            )}
          </span>
          <span className="flex-shrink-0 text-black/40">{getTaskIcon(task.type)}</span>
          <span className="flex-1 min-w-0">
            <span
              className={`block text-sm font-medium truncate ${
                task.completed ? 'text-black/45 line-through' : 'text-black'
              }`}
            >
              {task.title}
            </span>
            <span className="block text-xs text-black/40 mt-0.5 line-clamp-2">{categoriesLine}</span>
          </span>
          <ChevronRight className="w-4 h-4 text-black/30 flex-shrink-0" />
        </button>
      </li>
    );
  };

  return (
    <div className="mb-6 sm:mb-8">
      <style>{`
        @keyframes taskFadeSlideIn {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          @keyframes taskFadeSlideIn { from { opacity: 0; } to { opacity: 1; } }
        }
      `}</style>

      <h2 className="font-space text-xl sm:text-2xl font-black text-black mb-1 tracking-tight">
        {t('practice.recommendedBanner.recommendedForThisWeek')}
      </h2>
      <p className="text-xs text-black/40 mb-3">
        {t('practice.recommendedBanner.weekOfTotal', { week: planData.currentWeekNumber, total: planData.totalWeeks })}
      </p>

      <ul className="bg-white rounded-xl border-2 border-black/10 overflow-hidden divide-y divide-black/5">
        {/* Always-visible first 3 */}
        {visibleTasks.map((task, i) => renderTaskRow(task, i))}

        {/* Animated extra items */}
        {hasMore && (
          <li className="p-0">
            <ExpandSection expanded={expanded}>
              <ul className="divide-y divide-black/5">
                {hiddenTasks.map((task, i) => renderTaskRow(task, i, true))}
              </ul>
            </ExpandSection>
          </li>
        )}

        {/* Toggle button */}
        {hasMore && (
          <li>
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="w-full flex items-center justify-center gap-1.5 px-4 py-3 min-h-[48px] text-sm font-medium text-black/50 hover:text-black/70 hover:bg-black/[0.02] active:bg-black/[0.04] transition-colors touch-manipulation"
            >
              <ChevronDown
                className="w-4 h-4 text-black/40 transition-transform duration-300"
                style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
              />
              <span className="transition-all duration-200">
                {expanded
                  ? t('practice.recommendedBanner.showLess', { defaultValue: 'Show less' })
                  : t('practice.recommendedBanner.showMore', {
                      defaultValue: '{{count}} more',
                      count: hiddenTasks.length,
                    })}
              </span>
            </button>
          </li>
        )}
      </ul>
    </div>
  );
};

export default RecommendedSessionBanner;
